import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import { DocImage, FeedbackCross, ViewIcon } from '../../../assets/svg';
import {
    popup,
    formValidator,
    getLocalStorageItem,
    validateFiles,
    getContentTypeByExtension,
    getFileExtensionFromPath,
    signedDigitalDeliveryFile,
    createDigitalDelivery,
    getAllCustomer,
    getFileSignedUrl,
} from '../../../utils';
import {
    REGEX,
    VALIDATION,
    MODAL,
    BUTTONS,
    DOCUMENT_REQUEST_FORM,
    SUCCESS,
    DIGITAL_SAFETY_LIBRARY,
    MAX_LENGTH,
    CUSTOMER_LIST,
    LOCAL_STORAGE,
    QUERY_KEYS,
} from '../../../constants';
import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../../../redux';
import SSCSelect from '../SSCSelect';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const FILE_LIMIT = 1;

const extractFileName = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
        return fileName;
    }
    return fileName.substring(0, lastDotIndex);
};

const DEFAULT_SIGNED_URL_VALUE = { preSignedUrl: '', filePath: '' };

function renameFileWithUUID(filename, uuid) {
    const lastIndex = filename.lastIndexOf('.');
    const fileExtension = filename.substring(lastIndex);
    return `${uuid}${fileExtension}`;
}

export const UploadDigitalDelivery = ({ showAddModal, setShowAddModal }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const [signedURL, setSignedURL] = useState(DEFAULT_SIGNED_URL_VALUE);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fullFileName, setFullFileName] = useState('');
    const [fileName, setFileName] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchCustomer, setSearchCustomer] = useState('');
    const [isFormFocused, setIsFormFocused] = useState(false);
    const uploaderId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const csmCaseload = useSelector((s) => s.csmCaseload?.csmCaseloadData);

    const validationSchema = {
        fileName: {
            regex: REGEX.FILE_NAME,
            message: VALIDATION.FILE_NAME,
            requiredMessage: VALIDATION.FILE_NAME_REQUIRED,
        },
    };

    const [debouncedSearch] = useDebouncedValue(searchCustomer, 500);
    const {
        data: customer,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS, debouncedSearch],
        queryFn: async () =>
            await getAllCustomer({
                offset: 1,
                limit: 50,
                search: debouncedSearch,
                id: csmCaseload?.id || '',
                role: csmCaseload?.role_code || '',
                hasPrimaryUser: true,
            }),
        select: (data) =>
            data.data.length > 0
                ? data?.data?.map((customer) => ({ label: customer.name, value: customer }))
                : [],
        enabled: showAddModal,
    });
    if (isError) popup('error', error.message);

    const handleClose = (data) => {
        if (isEmpty(data)) {
            setSelectedCustomer(null);
            return;
        }
        setSelectedCustomer({ id: data.id, name: data.name });
        setSearchCustomer('');
    };

    const validateForm = () => setIsFormFocused(true);

    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles((prevSelectedFiles) =>
            prevSelectedFiles.filter((_, index) => index !== indexToRemove),
        );
        setFileName('');
        setFullFileName('');
        setSignedURL(DEFAULT_SIGNED_URL_VALUE);
    };

    const onFileChange = (event, setFieldValue) => {
        try {
            const files = event.target.files;
            validateFiles(
                files,
                selectedFiles,
                DOCUMENT_REQUEST_FORM.FILE.FILE_SIZE_LIMIT_25MB,
                FILE_LIMIT,
            );
            const selectedFilesArray = Array.from(files);
            setFullFileName(selectedFilesArray[0]?.name);
            setFileName(extractFileName(selectedFilesArray[0]?.name));
            setFieldValue('fileName', extractFileName(selectedFilesArray[0]?.name));
            setSelectedFiles([...selectedFilesArray]);
        } catch (error) {
            popup('error', error.message);
        }
    };

    // 1.) To dynamically creates the <input> element,
    // 2.) Don't need the input to persist in the DOM but only need its functionality.
    const openFileExplorer = (setFieldValue) => {
        if (selectedFiles.length >= FILE_LIMIT) {
            popup(SUCCESS.SUCCESS_TOAST, `Please upload only ${FILE_LIMIT} file at a time.`);
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = (e) => onFileChange(e, setFieldValue);
            input.click();
        }
    };

    const handleFileNameChange = (event, setFieldValue) => {
        setFileName(event.target.value);
        setFieldValue('fileName', event.target.value);
    };

    const readFileAsArrayBuffer = async (file) => {
        try {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            return new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const handlePreview = async () => {
        if (selectedFiles[0]) {
            const fileType = selectedFiles[0]?.type;
            const uri = URL.createObjectURL(selectedFiles[0]);
            if (
                fileType === 'application/vnd.ms-excel' ||
                fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                fileType === 'application/msword' ||
                fileType ===
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ) {
                try {
                    dispatch(setLoaderVisibility(true));
                    const fileUUID = uuidV4();
                    const signedData = {
                        file_name: renameFileWithUUID(fullFileName, fileUUID),
                        account_id: selectedCustomer?.id,
                    };
                    const binaryData = await readFileAsArrayBuffer(selectedFiles[0]);

                    const {
                        data: { preSignedUrl, filePath },
                    } = await signedDigitalDeliveryFile({
                        signedData,
                    });

                    setSignedURL({ preSignedUrl, filePath });

                    const uploadResponse = await fetch(preSignedUrl, {
                        method: 'PUT',
                        body: binaryData,
                        headers: {
                            'Content-Type': fileType,
                        },
                    });
                    if (uploadResponse.status === 200) {
                        const { data } = await getFileSignedUrl({ filePath });
                        dispatch(
                            setFileViewModalDetails({
                                openFileViewerModal: true,
                                signedURL: data.url,
                                fileName,
                            }),
                        );
                    }
                } catch (error) {
                    popup('error', error.message);
                } finally {
                    dispatch(setLoaderVisibility(false));
                }
            } else {
                dispatch(
                    setFileViewModalDetails({
                        openFileViewerModal: true,
                        docs: [{ uri, fileType }],
                        fileName,
                    }),
                );
            }
        }
    };

    const createDigitalDeliveryData = async (values, filePath) => {
        const createDDData = {
            file_name: values.fileName,
            s3_key: filePath,
            account_id: selectedCustomer?.id,
            uploaded_by: uploaderId,
        };
        const { message } = await createDigitalDelivery({ createDDData });
        return message;
    };

    const resetFormState = () => {
        setShowAddModal(false);
        setFileName('');
        setFullFileName('');
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.DIGITAL_SAFETY_MANUAL],
            exact: false,
        });
    };

    return (
        <Formik
            initialValues={{
                fileName: fileName,
            }}
            validate={(values) => {
                const errors = {};
                formValidator({ values, errors, validationSchema });
                return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
                dispatch(setLoaderVisibility(true));
                dispatch(setShowProgress(true));
                setSubmitting(true);
                try {
                    if (!values.fileName || !fileName || !uploaderId || !selectedCustomer?.id) {
                        throw new Error('Required fields are missing');
                    }

                    if (!signedURL.filePath) {
                        // 1.) Get presigned URL
                        const fileUUID = uuidV4();
                        const signedData = {
                            file_name: renameFileWithUUID(fullFileName, fileUUID),
                            account_id: selectedCustomer?.id,
                        };
                        const file = selectedFiles[0]; // single file
                        if (!file) throw new Error('No file selected');

                        const {
                            data: { preSignedUrl, filePath },
                        } = await signedDigitalDeliveryFile({
                            signedData,
                        });
                        if (!preSignedUrl) throw new Error('No signed URL received');

                        // 2. Upload file
                        await new Promise((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', preSignedUrl, true);
                            xhr.setRequestHeader(
                                'Content-Type',
                                getContentTypeByExtension(getFileExtensionFromPath(filePath)),
                            );

                            xhr.upload.onprogress = (e) => {
                                if (e.lengthComputable) {
                                    const percent = Math.round((e.loaded / e.total) * 100);
                                    dispatch(setLoaderProgress(percent));
                                }
                            };

                            xhr.onload = () => {
                                if (xhr.status === 200) {
                                    resolve();
                                } else {
                                    reject(new Error(`Upload failed: ${xhr.status}`));
                                }
                            };
                            xhr.onerror = () => reject(new Error(`XHR error on file ${file.name}`));
                            xhr.send(file);
                        });

                        const message = await createDigitalDeliveryData(values, filePath);
                        popup('success', message);
                        resetFormState();
                        
                    } else if (signedURL.filePath) {
                        const message = await createDigitalDeliveryData(
                            values,
                            signedURL?.filePath,
                        );
                        popup('success', message);
                        resetFormState();
                    }
                } catch (error) {
                    popup('error', error.message);
                } finally {
                    setSubmitting(false);
                    dispatch(setLoaderVisibility(false));
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleSubmit,
                dirty,
                isSubmitting,
                setTouched,
                setFieldValue,
            }) => (
                <div
                    className={`modal custom-modal-doc-cato fade ${showAddModal ? 'show' : ''}`}
                    id='exampleModal-add'
                    tabIndex={-1}
                    aria-labelledby='exampleModalLabel'
                    aria-hidden={!showAddModal}
                    style={{ display: showAddModal ? 'block' : 'none' }}
                >
                    <div className='modal-dialog w-100 custom-wid '>
                        <div className={`modal-content border-0`}>
                            <div className='d-flex align-items-center justify-content-between'>
                                <span className='head-title-edit'>
                                    {DIGITAL_SAFETY_LIBRARY.MODAL.HEADING}
                                </span>
                                <button
                                    type='button'
                                    className='btn-close'
                                    data-bs-dismiss='modal'
                                    aria-label='Close'
                                    onClick={() => {
                                        setShowAddModal(false);
                                    }}
                                />
                            </div>
                            <div
                                className='modal-body customer-form p-0'
                                onFocus={() => validateForm()}
                            >
                                <form action='' className='customer-from-content'>
                                    <div className='row'>
                                        <div className='col-12 mb-3'>
                                            <SSCSelect
                                                required
                                                isClearable
                                                selectStyle={{ height: '62px' }}
                                                label={CUSTOMER_LIST.PAGE_HEADING}
                                                options={customer}
                                                onChange={(d) => handleClose(d?.value)}
                                                onInputChange={(v) => {
                                                    if (!isEmpty(v)) {
                                                        setSearchCustomer(v);
                                                    }
                                                }}
                                                isLoading={isFetching}
                                                value={
                                                    selectedCustomer?.id
                                                        ? {
                                                              label: selectedCustomer.name,
                                                              value: customer.find(
                                                                  (c) =>
                                                                      c.id === selectedCustomer.id,
                                                              ),
                                                          }
                                                        : null
                                                }
                                                isOptionSelected={(option) =>
                                                    option.value?.id === selectedCustomer?.id
                                                }
                                                formatOptionLabel={({ label, value }) =>
                                                    value?.email ? (
                                                        <div className='d-flex flex-column'>
                                                            <span>{label}</span>
                                                            <span
                                                                style={{ fontSize: '12px' }}
                                                                className='text-black-50'
                                                            >
                                                                {value?.email}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div>{label}</div>
                                                    )
                                                }
                                                onBlur={(e) => {
                                                    const blurValue = e.target.value;
                                                    if (isEmpty(blurValue)) setSearchCustomer('');
                                                }}
                                            />
                                        </div>
                                        {selectedFiles.length > 0 && (
                                            <div className='col-12'>
                                                <div className='input-set-area mb-4'>
                                                    <label htmlFor='fileName'>
                                                        {DIGITAL_SAFETY_LIBRARY.FILE_NAME}
                                                    </label>
                                                    <input
                                                        maxLength={MAX_LENGTH.NAME}
                                                        type='text'
                                                        name='fileName'
                                                        onChange={(e) => {
                                                            !touched.fileName &&
                                                                setTouched({
                                                                    ...touched,
                                                                    fileName: true,
                                                                });
                                                            handleFileNameChange(e, setFieldValue);
                                                        }}
                                                        onBlur={handleBlur}
                                                        value={values.fileName}
                                                        className={`new-customer-input input-area-set form-control ${isFormFocused && errors.fileName && touched.fileName && errors.fileName ? 'border border-danger-subtle border-1' : ''}`}
                                                        placeholder={
                                                            DIGITAL_SAFETY_LIBRARY.FILE_NAME_PLACEHOLDER
                                                        }
                                                    />
                                                    {isFormFocused &&
                                                        touched.fileName &&
                                                        errors.fileName && (
                                                            <p className='error-message'>
                                                                {errors.fileName}
                                                            </p>
                                                        )}
                                                </div>
                                            </div>
                                        )}
                                        <div className='d-flex align-items-center justify-content-between col-4'>
                                            {selectedFiles.length === 0 && (
                                                <button
                                                    className='ssc-primary-green-btn upload-file-btn disabled-hover '
                                                    type='button'
                                                    onClick={() => openFileExplorer(setFieldValue)}
                                                    disabled={
                                                        selectedFiles.length ===
                                                            DOCUMENT_REQUEST_FORM.FILE.FILE_LIMIT ||
                                                        !selectedCustomer?.id
                                                    }
                                                    style={{
                                                        height: '48px',
                                                        fontSize: '16px',
                                                        minWidth: 'max-content',
                                                    }}
                                                >
                                                    {DOCUMENT_REQUEST_FORM.FILE.UPLOAD}
                                                </button>
                                            )}

                                            {selectedFiles.length > 0 && (
                                                <div className='d-flex gap-2 align-items-center position-relative'>
                                                    {selectedFiles.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className='d-flex  align-items-start flex-column position-relative'
                                                        >
                                                            <DocImage />
                                                            {file.name.length < 10 ? (
                                                                <span
                                                                    title={file.name}
                                                                    className='mt-2'
                                                                >
                                                                    {file.name}
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className='truncate-text mt-2'
                                                                    title={file.name}
                                                                >
                                                                    {file.name.slice(0, 6) +
                                                                        '...' +
                                                                        file.name.split('.')[1]}
                                                                </span>
                                                            )}
                                                            <button
                                                                type='button'
                                                                className='position-absolute'
                                                                style={{
                                                                    bottom: '20px',
                                                                    right: '-5px',
                                                                    background: 'white',
                                                                }}
                                                                id='action-btn'
                                                                onClick={handlePreview}
                                                                title='View'
                                                            >
                                                                <ViewIcon />
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='position-absolute'
                                                                style={{
                                                                    top: '-12px',
                                                                    right: '-6px',
                                                                }}
                                                                id='action-btn'
                                                                onClick={() =>
                                                                    handleRemoveFile(index)
                                                                }
                                                                title='Remove'
                                                            >
                                                                <FeedbackCross />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <div className='d-flex align-items-center justify-content-end modal-btn mt-3'>
                                <button
                                    type='button'
                                    className='ssc-secondary-white-btn '
                                    onClick={() => {
                                        setShowAddModal(false);
                                    }}
                                >
                                    {MODAL.CANCEL_BUTTON}
                                </button>
                                <button
                                    type='submit'
                                    onClick={handleSubmit}
                                    className='ssc-primary-green-btn py-0 w-auto disabled-hover '
                                    disabled={
                                        isSubmitting ||
                                        !dirty ||
                                        errors.fileName ||
                                        !selectedCustomer?.id ||
                                        selectedFiles.length !== FILE_LIMIT
                                    }
                                >
                                    {BUTTONS.SAVE_BUTTON}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Formik>
    );
};

UploadDigitalDelivery.propTypes = {
    showAddModal: PropTypes.bool,
    setShowAddModal: PropTypes.func,
};
